import React, { useState } from 'react';
import { 
  Bot, Check, Crown, MessageSquare, FileText, Search,
  Clock, AlertTriangle, ChevronRight, Info, Pause
} from 'lucide-react';
import GlassPanel from '../components/GlassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier configuration - Locked pricing
const tierConfig = {
  none: {
    name: 'No Subscription',
    price: '£0/month',
    features: [],
    color: 'var(--text-muted)'
  },
  basic: {
    name: 'Assistant-Life Basic',
    price: '£5/month',
    description: 'Light use',
    features: [
      'Acknowledge customer messages',
      'Gather missing information',
      'Organise & categorise communications',
      'Draft replies for review',
      'Daily summaries'
    ],
    color: 'var(--accent-primary)'
  },
  plus: {
    name: 'Assistant-Life Plus',
    price: '£15/month',
    description: 'Heavy use, automation, emergency access',
    features: [
      'All Basic features',
      'Material price research',
      'Overnight quote preparation',
      'Overnight invoice preparation',
      'Emergency Mode access'
    ],
    recommended: true,
    color: 'var(--status-success)'
  },
  pro: {
    name: 'Assistant-Life Pro',
    price: 'Included in Pro',
    description: 'Included in Pro tiers',
    features: [
      'All Plus features',
      'Auto-send approved replies (where configured)'
    ],
    color: 'var(--status-warning)'
  }
};

// Assistant states as per spec
const assistantStates = {
  idle: { label: 'Idle', description: 'No autonomous actions' },
  drafting: { label: 'Drafting', description: 'Draft visible but not sent' },
  sent: { label: 'Sent', description: 'Clearly marked as assistant-sent' },
  suspended: { label: 'Suspended', description: 'Emergency Override - Assistant stopped' }
};

// Mock subscription
const mockSubscription = {
  tier: 'plus',
  is_active: true
};

// Mock actions with proper labelling
const mockActions = [
  {
    id: '1',
    action_type: 'draft_reply',
    status: 'drafting', // Draft visible but not sent
    response: 'Thank you for your enquiry regarding the bathroom renovation. I have noted your requirements and will arrange for a site visit to provide an accurate estimate.',
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: '2',
    action_type: 'material_research',
    status: 'sent',
    response: 'Ceramic wall tiles (standard 300x600mm):\nIndicative range: £15-35 per sqm\n\nTrade price pending - this is an indicative public price range only.',
    created_at: new Date(Date.now() - 7200000).toISOString()
  }
];

export function AssistantLife() {
  const [subscription, setSubscription] = useState(mockSubscription);
  const [actions, setActions] = useState(mockActions);
  const [selectedAction, setSelectedAction] = useState(null);
  const [assistantState, setAssistantState] = useState('idle');

  const currentTier = tierConfig[subscription.tier] || tierConfig.none;

  const handleSubscribe = (tier) => {
    setSubscription({ tier, is_active: true });
  };

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="page" data-testid="assistant-life">
      <div className="page-header">
        <div className="page-header-content">
          <h1 data-testid="assistant-life-title">
            <Bot size={24} style={{ marginRight: '8px', display: 'inline', color: 'var(--accent-primary)' }} />
            Assistant-Life
          </h1>
          <p>Virtual office assistant - handles admin, never makes decisions</p>
        </div>
        <div className="page-header-actions">
          {subscription.is_active && (
            <div style={{
              background: 'var(--accent-primary-muted)',
              padding: 'var(--space-sm) var(--space-md)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--accent-primary)',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              <Crown size={14} style={{ marginRight: '6px', display: 'inline' }} />
              {currentTier.name}
            </div>
          )}
        </div>
      </div>

      {/* Assistant State Indicator */}
      <div className="glass-panel" style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: assistantState === 'suspended' ? 'var(--status-danger)' : 
                         assistantState === 'idle' ? 'var(--text-dimmed)' : 'var(--status-success)'
            }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Assistant Status: <strong>{assistantStates[assistantState]?.label}</strong>
            </span>
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {assistantStates[assistantState]?.description}
          </span>
        </div>
      </div>

      {/* DISALLOWED Actions Panel - Clearly visible */}
      <div className="ai-panel" style={{ marginBottom: 'var(--space-xl)' }} data-testid="guardrails-panel">
        <div className="ai-panel-header">
          <AlertTriangle size={20} className="ai-panel-icon" style={{ color: 'var(--status-danger)' }} />
          <span className="ai-panel-title">Assistant Guardrails (Non-Negotiable)</span>
        </div>
        <div className="responsive-grid-2" style={{ gap: 'var(--space-xl)' }}>
          <div>
            <h4 style={{ color: 'var(--status-success)', marginBottom: 'var(--space-sm)', fontSize: '0.875rem' }}>
              <Check size={14} style={{ marginRight: '6px', display: 'inline' }} />
              CAN DO
            </h4>
            <ul style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', paddingLeft: '20px', margin: 0 }}>
              <li>Acknowledge customer messages</li>
              <li>Gather missing information</li>
              <li>Organise & categorise communications</li>
              <li>Draft replies (for your review)</li>
              <li>Research indicative material prices</li>
              <li>Produce daily summaries</li>
            </ul>
          </div>
          <div>
            <h4 style={{ color: 'var(--status-danger)', marginBottom: 'var(--space-sm)', fontSize: '0.875rem' }}>
              <AlertTriangle size={14} style={{ marginRight: '6px', display: 'inline' }} />
              CANNOT DO (Never)
            </h4>
            <ul style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', paddingLeft: '20px', margin: 0 }}>
              <li><strong>Agree pricing</strong></li>
              <li><strong>Agree scope of work</strong></li>
              <li><strong>Commit timelines</strong></li>
              <li><strong>Approve variations or works</strong></li>
              <li><strong>Speak on behalf of trades without review</strong></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Subscription Tiers */}
      {!subscription.is_active && (
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: 'var(--space-md)', color: 'var(--text-primary)' }}>
            Choose Your Plan
          </h2>
          <div className="responsive-grid-3" style={{ gap: 'var(--space-lg)' }}>
            {['basic', 'plus', 'pro'].map(tier => {
              const config = tierConfig[tier];
              return (
                <div 
                  key={tier}
                  className="glass-panel"
                  style={{
                    border: config.recommended ? `2px solid ${config.color}` : undefined,
                    position: 'relative'
                  }}
                  data-testid={`tier-${tier}`}
                >
                  {config.recommended && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: config.color,
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '0.625rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      Recommended
                    </div>
                  )}
                  <h3 style={{ color: config.color, marginBottom: 'var(--space-xs)' }}>{config.name}</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: 'var(--space-md)' }}>
                    {config.price}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
                    {config.description}
                  </p>
                  <ul style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', paddingLeft: '20px', marginBottom: 'var(--space-lg)' }}>
                    {config.features.map((f, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>
                        <Check size={12} style={{ color: 'var(--status-success)', marginRight: '6px', display: 'inline' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button 
                    className={`btn ${config.recommended ? 'btn-primary' : 'btn-secondary'} w-full`}
                    onClick={() => handleSubscribe(tier)}
                    data-testid={`subscribe-${tier}`}
                  >
                    Subscribe
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Actions - All actions visible in audit trail */}
      <div className="responsive-grid-2" style={{ gap: 'var(--space-lg)' }}>
        <GlassPanel title="Recent Actions (Audit Trail)" titleIcon={Clock}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
            All assistant actions are logged and visible
          </div>
          {actions.length > 0 ? (
            <div className="list-container">
              {actions.map(action => (
                <div 
                  key={action.id}
                  className="list-item"
                  onClick={() => setSelectedAction(action)}
                  data-testid={`action-${action.id}`}
                >
                  <div className="list-item-content">
                    {/* Clear label: Assistant (Draft / Sent) */}
                    <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      <Bot size={14} style={{ color: 'var(--accent-primary)' }} />
                      <span>Assistant</span>
                      <span style={{
                        fontSize: '0.625rem',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: action.status === 'drafting' ? 'var(--status-warning-bg)' : 'var(--status-success-bg)',
                        color: action.status === 'drafting' ? 'var(--status-warning)' : 'var(--status-success)',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {action.status === 'drafting' ? 'Draft' : 'Sent'}
                      </span>
                    </div>
                    <div className="list-item-subtitle" style={{ textTransform: 'capitalize' }}>
                      {action.action_type.replace(/_/g, ' ')} • {formatTimestamp(action.created_at)}
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
              No actions yet
            </div>
          )}
        </GlassPanel>

        {/* Quick Actions */}
        <GlassPanel title="Quick Actions" titleIcon={Bot}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <button 
              className="btn btn-secondary w-full" 
              style={{ justifyContent: 'flex-start' }}
              disabled={!subscription.is_active || assistantState === 'suspended'}
              data-testid="action-draft-reply"
            >
              <MessageSquare size={16} />
              Draft Reply
            </button>
            <button 
              className="btn btn-secondary w-full" 
              style={{ justifyContent: 'flex-start' }}
              disabled={!subscription.is_active || subscription.tier === 'basic' || assistantState === 'suspended'}
              data-testid="action-research-material"
            >
              <Search size={16} />
              Research Material Price
              {subscription.tier === 'basic' && (
                <span style={{ marginLeft: 'auto', fontSize: '0.625rem', color: 'var(--status-warning)' }}>Plus+</span>
              )}
            </button>
            <button 
              className="btn btn-secondary w-full" 
              style={{ justifyContent: 'flex-start' }}
              disabled={!subscription.is_active || assistantState === 'suspended'}
              data-testid="action-daily-summary"
            >
              <FileText size={16} />
              Generate Daily Summary
            </button>
          </div>

          {/* Suspended State Notice */}
          {assistantState === 'suspended' && (
            <div style={{
              marginTop: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'var(--status-danger-bg)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--status-danger)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', color: 'var(--status-danger)' }}>
                <Pause size={16} />
                <span style={{ fontWeight: '600' }}>Emergency Override Active</span>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 'var(--space-sm) 0 0' }}>
                Assistant has stopped all non-emergency behaviour during active emergency.
              </p>
            </div>
          )}

          {/* Audit Notice */}
          <div className="ai-explanation-box" style={{ marginTop: 'var(--space-lg)' }}>
            <Info size={16} className="ai-explanation-icon" />
            <p>All Assistant-Life actions are logged, visible, and auditable. Nothing happens without your knowledge.</p>
          </div>
        </GlassPanel>
      </div>

      {/* Selected Action Detail */}
      {selectedAction && (
        <div className="glass-panel" style={{ marginTop: 'var(--space-lg)' }} data-testid="action-detail">
          <div className="glass-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Bot size={18} style={{ color: 'var(--accent-primary)' }} />
              <span className="glass-panel-title">
                Assistant ({selectedAction.status === 'drafting' ? 'Draft' : 'Sent'})
              </span>
              <span style={{
                fontSize: '0.625rem',
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                background: selectedAction.status === 'drafting' ? 'var(--status-warning-bg)' : 'var(--status-success-bg)',
                color: selectedAction.status === 'drafting' ? 'var(--status-warning)' : 'var(--status-success)',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                {selectedAction.status === 'drafting' ? 'Draft - Not Sent' : 'Sent'}
              </span>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={() => setSelectedAction(null)}>
              ×
            </button>
          </div>
          
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
            {selectedAction.action_type.replace(/_/g, ' ')} • {formatTimestamp(selectedAction.created_at)}
          </div>
          
          <div style={{
            background: 'var(--bg-tertiary)',
            padding: 'var(--space-lg)',
            borderRadius: 'var(--radius-md)',
            whiteSpace: 'pre-wrap',
            fontSize: '0.9375rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.6
          }}>
            {selectedAction.response}
          </div>
          
          {selectedAction.status === 'drafting' && (
            <div style={{ marginTop: 'var(--space-lg)', display: 'flex', gap: 'var(--space-md)' }}>
              <button className="btn btn-primary" data-testid="approve-draft">
                <Check size={16} />
                Approve & Send
              </button>
              <button className="btn btn-secondary" data-testid="edit-draft">
                Edit Draft
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AssistantLife;
